import Highlighter from 'react-highlight-words'
import memoize from 'fast-memoize'
import {MenuProps} from 'antd'
import useStore from 'base/store'
import {
  Box,
  File,
  Dropdown,
  Modal,
  IconButton,
  DotsSecondIcon,
  ArrowShortSecondIcon,
} from 'common/components'
import {useOpen} from 'common/hooks'
import {
  DocumentRenameForm,
  DocumentsMenuLabel,
  useArchiveDocument,
  useCopyDocument,
  getDrawdownCollections,
  DOCUMENTS_CATEGORY,
  DOCUMENTS_COLLECTION,
  IDocument,
  IDocumentsTab,
  IDocumentsCollection,
  IDocumentsCategory,
} from '..'

interface DocumentProps {
  projectUuid: string
  searchPage?: boolean
  search?: string
  document: IDocument
  drawdownsTabs?: IDocumentsTab[]
  amlTabs?: IDocumentsTab[]
}

interface HighlightedNameProps {
  searchPage?: boolean
  search: string
  document: IDocument
}

interface DocumentMenuProps {
  document: IDocument
  projectUuid: string
  drawdownsTabs?: IDocumentsTab[]
  amlTabs?: IDocumentsTab[]
  onRenameOpen: () => void
}

export default function Document({
  searchPage,
  search,
  projectUuid,
  document,
  drawdownsTabs,
  amlTabs,
}: DocumentProps) {
  const role = useStore((state) => state.role)
  const {mutate: archiveDocument, isMutating: isArchiveDocumentMutating} =
    useArchiveDocument(document.uuid, projectUuid)
  const [isRenameOpen, handleRenameOpen, handleRenameClose] = useOpen(false)

  const isEditable = !document.archived

  const isRemovable = Boolean(
    isEditable &&
      !isAMLChatDocument(document) &&
      document.createdBy &&
      document.createdBy.role !== 'user' &&
      !role?.user
  )

  const isSecondary =
    document.collection === 'attachment' || document.createdBy?.role === 'user'

  const drawdownName =
    searchPage && document.model?.type === 'drawdown'
      ? document.model.title
      : undefined

  return (
    <>
      <File
        to={searchPage ? getLinkTo(projectUuid, document) : undefined}
        secondary={isSecondary}
        highlightedName={
          search ? (
            <HighlightedName
              searchPage={searchPage}
              search={search}
              document={document}
            />
          ) : undefined
        }
        name={document.name}
        size={document.size}
        url={document.url}
        userName={document.createdBy?.firstName}
        createdAt={document.createdAt}
        drawdownName={drawdownName}
        menu={
          isEditable ? (
            <DocumentMenu
              document={document}
              projectUuid={projectUuid}
              drawdownsTabs={drawdownsTabs}
              amlTabs={amlTabs}
              onRenameOpen={handleRenameOpen}
            />
          ) : document.archived ? (
            <Dropdown
              menu={{
                items: [{key: 'unarchive', label: 'Unarchive'}],
                onClick: archiveDocument,
              }}
            >
              <IconButton
                className="File-Action"
                icon={<DotsSecondIcon />}
                loading={isArchiveDocumentMutating}
              />
            </Dropdown>
          ) : undefined
        }
        isRemoveLoading={isRemovable ? isArchiveDocumentMutating : undefined}
        onRemove={isRemovable ? archiveDocument : undefined}
      />
      {!isAMLChatDocument(document) && (
        <Modal
          width={486}
          title="Rename document"
          open={isRenameOpen}
          onCancel={handleRenameClose}
        >
          <DocumentRenameForm
            document={document}
            projectUuid={projectUuid}
            onClose={handleRenameClose}
          />
        </Modal>
      )}
    </>
  )
}

const HighlightedName = ({
  searchPage,
  search,
  document,
}: HighlightedNameProps) => {
  const highlighter = (
    <Highlighter
      autoEscape
      searchWords={[search]}
      textToHighlight={document.name}
    />
  )

  if (!searchPage) return highlighter

  const collection = DOCUMENTS_COLLECTION[document.collection]
  const category = DOCUMENTS_CATEGORY[collection?.categoryKey]
  const categoryOrder = category?.order
  const collectionOrder = isAMLCategory(category)
    ? document.model.sortOrder
    : isCategoryMultipleCollections(category)
    ? collection?.categoryOrder
    : null
  const categoryLabel = category?.label
  const collectionLabel = isAMLCategory(category)
    ? document.model.title
    : isCategoryMultipleCollections(category)
    ? collection?.label
    : null

  return (
    <span>
      <>
        {categoryOrder}
        {collectionOrder && `.${collectionOrder}`}{' '}
        {categoryLabel && `${categoryLabel} /`}{' '}
        {collectionLabel && `${collectionLabel} /`}{' '}
      </>
      {highlighter}
    </span>
  )
}

const DocumentMenu = ({
  document,
  projectUuid,
  drawdownsTabs,
  amlTabs,
  onRenameOpen,
}: DocumentMenuProps) => {
  const isChatDocument = isAMLChatDocument(document)
  const {mutate: copyDocument, isMutating: isCopyDocumentMutating} =
    useCopyDocument(document.uuid, projectUuid, isChatDocument)
  const menuCategoriesItems = memoizedGetMenuCategoriesItems(
    drawdownsTabs,
    amlTabs
  )

  const menu: MenuProps = {
    expandIcon: (
      <Box as="span" color="atmo">
        <ArrowShortSecondIcon />
      </Box>
    ),
    items: [
      ...[isChatDocument ? undefined : {key: renameActionKey, label: 'Rename'}],
      {
        key: 'copyTo',
        label: 'Copy to...',
        type: 'group',
        children: menuCategoriesItems,
      },
    ],
    onClick: ({key, keyPath, domEvent}) => {
      domEvent.preventDefault()
      domEvent.stopPropagation()
      if (key === renameActionKey) return onRenameOpen()
      copyDocument({key, keyPath})
    },
  }

  return (
    <Dropdown menu={menu}>
      <IconButton
        className="File-Action"
        icon={<DotsSecondIcon />}
        loading={isCopyDocumentMutating}
      />
    </Dropdown>
  )
}

const isAMLCategory = (category: IDocumentsCategory) =>
  category.key === 'amlDocuments'

const isCategoryMultipleCollections = (category: IDocumentsCategory) =>
  category?.collections?.length > 1

const getLinkTo = (projectUuid: string, document: IDocument) => {
  const collection = DOCUMENTS_COLLECTION[document.collection]
  const category = DOCUMENTS_CATEGORY[collection?.categoryKey]
  return `/projects/${projectUuid}/documents${
    category && category.key !== 'amlDocuments' ? `/${category.key}` : ''
  }${
    isAMLCategory(category)
      ? `#${document.model.uuid}`
      : isCategoryMultipleCollections(category)
      ? `#${collection.key}`
      : ''
  }`
}

const getMenuCategoriesItems = (
  drawdownsTabs: IDocumentsTab[],
  amlTabs?: IDocumentsTab[]
) => {
  return menuCategories.map((category) => {
    const isDrawdownsCategory = category.key === 'drawdowns'
    const disabled =
      isDrawdownsCategory && (!drawdownsTabs || !drawdownsTabs.length)
    return {
      disabled,
      key: category.key,
      label: (
        <DocumentsMenuLabel number={category.order} label={category.label} />
      ),
      children: isDrawdownsCategory
        ? drawdownsTabs?.map((tab) => ({
            key: tab.collection,
            label: (
              <DocumentsMenuLabel
                number={`${category.order}.${tab.order}`}
                label={tab.label}
              />
            ),
            children: getDrawdownCollections(
              category.order,
              tab.order,
              tab.collection
            ),
          }))
        : category.key === 'amlDocuments'
        ? getAmlMenuCollections(amlTabs)
        : category.collections?.length > 1
        ? getMenuCategoryCollections(category.collections, category.order)
        : undefined,
    }
  })
}

const memoizedGetMenuCategoriesItems = memoize(getMenuCategoriesItems)

const getAmlMenuCollections = (amlTabs: IDocumentsTab[]) =>
  !amlTabs?.length ? []:
  amlTabs?.map((tab) => ({
    key: tab.collection,
    label: (
      <DocumentsMenuLabel
        number={`${DOCUMENTS_CATEGORY.amlDocuments.order}.${tab.order}`}
        label={tab.label}
      />
    ),
  }))

const getMenuCategoryCollections = (
  collections: IDocumentsCollection[],
  order: number
) =>
  collections.map((collection) => ({
    key: collection.key,
    label: (
      <DocumentsMenuLabel
        number={`${order}.${collection.categoryOrder}`}
        label={collection.label}
      />
    ),
  }))

const isAMLChatDocument = (document: IDocument) =>
  document.collection === 'attachment' ||
  document.collection === 'addressDocuments' ||
  document.collection === 'identityDocuments'

const menuCategories = Object.values(DOCUMENTS_CATEGORY).slice(0, -1)

const renameActionKey = 'rename'
